import apiClient from './api'
import {
  StockInfo,
  StockAdjustment,
  LowStockVariant,
  StockMovement,
  InventorySummary,
  StockUpdateRequest,
  StockAdjustmentRequest,
  BulkStockUpdate,
  BulkStockUpdateResponse,
  StockImportResult,
  StockReservation,
  LowStockThresholdUpdate,
  InventoryDashboardData,
  BalancingRecommendation,
  BalancingReportResponse,
  ReorderPointUpdate,
  BulkReorderPointUpdateRequest,
  BulkReorderPointUpdateResponse,
  BalancingFilters
} from '../types/inventory'

export class InventoryService {
  // Stock Information
  static async getVariantStock(variantId: string, location?: string): Promise<StockInfo> {
    const params = location ? { location } : {}
    const response = await apiClient.get(`/admin/inventory/stock/${variantId}`, { params })
    return response.data
  }

  static async getInventorySummary(categoryId?: string, productId?: string): Promise<InventorySummary> {
    const params: any = {}
    if (categoryId) params.category_id = categoryId
    if (productId) params.product_id = productId
    
    const response = await apiClient.get('/admin/inventory/summary', { params })
    return response.data
  }

  // Advanced Dashboard Data
  static async getInventoryDashboardData(): Promise<InventoryDashboardData> {
    const response = await apiClient.get('/admin/inventory/reports/summary-dashboard')
    return response.data
  }

  // Stock Adjustments
  static async updateStock(request: StockUpdateRequest): Promise<StockAdjustment> {
    const response = await apiClient.put('/admin/inventory/stock/update', request)
    return response.data
  }

  static async adjustStock(request: StockAdjustmentRequest): Promise<StockAdjustment> {
    const response = await apiClient.put('/admin/inventory/stock/adjust', request)
    return response.data
  }

  static async batchAdjustStock(request: import('../types/inventory').BatchStockAdjustmentRequest): Promise<import('../types/inventory').BatchStockAdjustmentResponse> {
    const response = await apiClient.post('/admin/inventory/adjustments/batch', request)
    return response.data
  }

  static async bulkUpdateStock(updates: BulkStockUpdate[]): Promise<BulkStockUpdateResponse> {
    const response = await apiClient.put('/admin/inventory/stock/bulk-update', { updates })
    return response.data
  }

  // Stock Reservations
  static async reserveStock(reservation: StockReservation): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post('/admin/inventory/stock/reserve', reservation)
    return response.data
  }

  static async releaseStockReservation(
    variantId: string,
    quantity: number,
    orderId: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post('/admin/inventory/stock/release-reservation', {
      variant_id: variantId,
      quantity,
      order_id: orderId
    })
    return response.data
  }

  static async fulfillStockReservation(
    variantId: string,
    quantity: number,
    orderId: string
  ): Promise<StockAdjustment> {
    const response = await apiClient.post('/admin/inventory/stock/fulfill-reservation', {
      variant_id: variantId,
      quantity,
      order_id: orderId
    })
    return response.data
  }

  // Low Stock Management
  static async getLowStockVariants(
    thresholdMultiplier: number = 1.0,
    limit: number = 100
  ): Promise<LowStockVariant[]> {
    const response = await apiClient.get('/admin/inventory/low-stock', {
      params: { threshold_multiplier: thresholdMultiplier, limit }
    })
    return response.data
  }

  static async updateStockThreshold(
    variantId: string,
    threshold: LowStockThresholdUpdate
  ): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.put(`/admin/inventory/threshold/${variantId}`, threshold)
    return response.data
  }

  // Stock Movements and Reports
  static async getStockMovements(
    variantId?: string,
    startDate?: string,
    endDate?: string,
    limit: number = 100
  ): Promise<StockMovement[]> {
    const params: any = { limit }
    if (variantId) params.variant_id = variantId
    if (startDate) params.start_date = startDate
    if (endDate) params.end_date = endDate

    const response = await apiClient.get('/admin/inventory/movements', { params })
    return response.data
  }

  // Import/Export
  static async importStockData(file: File): Promise<StockImportResult> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await apiClient.post('/admin/inventory/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  }

  static async exportStockData(categoryId?: string, productId?: string): Promise<Blob> {
    const params: any = {}
    if (categoryId) params.category_id = categoryId
    if (productId) params.product_id = productId

    const response = await apiClient.get('/admin/inventory/export', {
      params,
      responseType: 'blob'
    })
    return response.data
  }

  // Utility methods for frontend
  static async getAllStockInfo(
    page: number = 1,
    pageSize: number = 20,
    search?: string,
    categoryId?: string,
    lowStockOnly?: boolean
  ): Promise<{ items: StockInfo[]; total: number; page: number; pageSize: number }> {
    const params: any = {
      page,
      per_page: pageSize
    }
    
    if (search) params.search = search
    if (categoryId) params.category_id = categoryId
    if (lowStockOnly) params.low_stock_only = lowStockOnly

    const response = await apiClient.get('/admin/inventory/stock', { params })
    return {
      items: response.data.items || response.data,
      total: response.data.total || response.data.length,
      page: response.data.page || page,
      pageSize: response.data.per_page || pageSize
    }
  }

  static downloadExportFile(blob: Blob, filename?: string): void {
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename || `inventory_export_${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  // Advanced Stock Adjustment Methods
  static async getStockAdjustments(
    filters: import('../types/inventory').StockAdjustmentFilters = {},
    limit: number = 50,
    offset: number = 0
  ): Promise<{
    adjustments: import('../types/inventory').StockAdjustmentDetail[]
    total: number
    pagination: { limit: number; offset: number; has_more: boolean }
  }> {
    try {
      const params: any = { limit, offset }
      if (filters.adjustment_type && filters.adjustment_type !== 'all') params.adjustment_type = filters.adjustment_type
      if (filters.status && filters.status !== 'all') params.status = filters.status
      if (filters.warehouse_id) params.warehouse_id = filters.warehouse_id
      if (filters.category_id) params.category_id = filters.category_id
      if (filters.brand_id) params.brand_id = filters.brand_id
      if (filters.start_date) params.start_date = filters.start_date
      if (filters.end_date) params.end_date = filters.end_date
      if (filters.created_by_admin_id) params.created_by_admin_id = filters.created_by_admin_id

      const response = await apiClient.get('/admin/inventory/adjustments', { params })
      return response.data
    } catch (error) {
      throw error
    }
  }

  static async createStockAdjustment(
    adjustment: import('../types/inventory').StockAdjustmentCreate
  ): Promise<import('../types/inventory').StockAdjustmentDetail> {
    try {
      const response = await apiClient.post('/admin/inventory/adjustments', adjustment)
      return response.data
    } catch (error) {
      throw error
    }
  }

  static async getStockAdjustment(
    adjustmentId: string
  ): Promise<import('../types/inventory').StockAdjustmentDetail> {
    try {
      const response = await apiClient.get(`/admin/inventory/adjustments/${adjustmentId}`)
      return response.data
    } catch (error) {
      throw error
    }
  }

  static async approveStockAdjustment(
    adjustmentId: string
  ): Promise<import('../types/inventory').StockAdjustmentDetail> {
    try {
      const response = await apiClient.post(`/admin/inventory/adjustments/${adjustmentId}/approve`)
      return response.data
    } catch (error) {
      throw error
    }
  }

  static async getShrinkageReport(
    startDate?: string,
    endDate?: string
  ): Promise<import('../types/inventory').ShrinkageReport> {
    try {
      const params: any = {}
      if (startDate) params.start_date = startDate
      if (endDate) params.end_date = endDate

      const response = await apiClient.get('/admin/inventory/adjustments/shrinkage-report', { params })
      return response.data
    } catch (error) {
      throw error
    }
  }

  static async exportStockAdjustments(
    filters: import('../types/inventory').StockAdjustmentFilters = {},
    format: 'excel' | 'csv' = 'excel'
  ): Promise<Blob> {
    try {
      const params: any = { format }
      if (filters.adjustment_type && filters.adjustment_type !== 'all') params.adjustment_type = filters.adjustment_type
      if (filters.status && filters.status !== 'all') params.status = filters.status
      if (filters.start_date) params.start_date = filters.start_date
      if (filters.end_date) params.end_date = filters.end_date

      const response = await apiClient.get('/admin/inventory/adjustments/export', {
        params,
        responseType: 'blob'
      })
      return response.data
    } catch (error) {
      throw error
    }
  }

  // Inventory Balancing Methods
  static async getBalancingRecommendations(
    filters: BalancingFilters = {},
    limit: number = 50,
    offset: number = 0
  ): Promise<BalancingReportResponse> {
    try {
      const params: any = { limit, offset }
      if (filters.category_id) params.category_id = filters.category_id
      if (filters.brand_id) params.brand_id = filters.brand_id
      if (filters.warehouse_id) params.warehouse_id = filters.warehouse_id
      if (filters.stock_status && filters.stock_status !== 'all') params.stock_status = filters.stock_status
      if (filters.sales_velocity && filters.sales_velocity !== 'all') params.sales_velocity = filters.sales_velocity
      if (filters.confidence_level && filters.confidence_level !== 'all') params.confidence_level = filters.confidence_level

      const response = await apiClient.get('/admin/inventory/balancing/recommendations', { params })
      return response.data
    } catch (error) {
      // Return mock data if API is not available
      return {
        recommendations: [
          {
            variant_id: '1',
            variant_sku: 'PROD-001-RED-M',
            variant_name: 'Red Medium',
            product_name: 'Premium T-Shirt',
            category_name: 'Clothing',
            brand_name: 'Premium Brand',
            warehouse_name: 'Main Warehouse',
            current_stock: 45,
            reserved_stock: 10,
            available_stock: 35,
            minimum_stock: 50,
            reorder_point: 75,
            average_daily_sales_30d: 2.5,
            average_daily_sales_60d: 2.8,
            average_daily_sales_90d: 2.3,
            days_of_stock_remaining: 14,
            suggested_order_quantity: 100,
            sales_velocity: 'medium',
            trend_direction: 'stable',
            confidence_level: 'high',
            last_restock_date: '2024-10-15T10:30:00Z',
            lead_time_days: 7,
            safety_stock_multiplier: 1.5,
          },
          {
            variant_id: '2',
            variant_sku: 'PROD-002-BLUE-L',
            variant_name: 'Blue Large',
            product_name: 'Cotton Jeans',
            category_name: 'Clothing',
            brand_name: 'Denim Co',
            warehouse_name: 'Main Warehouse',
            current_stock: 15,
            reserved_stock: 5,
            available_stock: 10,
            minimum_stock: 40,
            reorder_point: 60,
            average_daily_sales_30d: 1.8,
            average_daily_sales_60d: 2.1,
            average_daily_sales_90d: 1.9,
            days_of_stock_remaining: 6,
            suggested_order_quantity: 80,
            sales_velocity: 'medium',
            trend_direction: 'increasing',
            confidence_level: 'high',
            last_restock_date: '2024-09-20T14:15:00Z',
            lead_time_days: 10,
            safety_stock_multiplier: 1.8,
          },
          {
            variant_id: '3',
            variant_sku: 'PROD-003-BLACK-S',
            variant_name: 'Black Small',
            product_name: 'Leather Jacket',
            category_name: 'Clothing',
            brand_name: 'Leather Works',
            warehouse_name: 'Main Warehouse',
            current_stock: 0,
            reserved_stock: 0,
            available_stock: 0,
            minimum_stock: 10,
            reorder_point: 20,
            average_daily_sales_30d: 0.5,
            average_daily_sales_60d: 0.7,
            average_daily_sales_90d: 0.6,
            days_of_stock_remaining: 0,
            suggested_order_quantity: 30,
            sales_velocity: 'slow',
            trend_direction: 'decreasing',
            confidence_level: 'medium',
            last_restock_date: '2024-08-10T09:00:00Z',
            lead_time_days: 14,
            safety_stock_multiplier: 2.0,
          },
        ],
        summary: {
          total_variants: 3,
          variants_needing_reorder: 2,
          total_suggested_order_value: 45000,
          fast_moving_variants: 0,
          slow_moving_variants: 1,
        },
        filters_applied: filters,
        pagination: {
          limit,
          offset,
          total: 3,
          has_more: false,
        },
      }
    }
  }

  static async updateReorderPoints(
    updates: ReorderPointUpdate[]
  ): Promise<BulkReorderPointUpdateResponse> {
    try {
      const response = await apiClient.post('/admin/inventory/balancing/update-reorder-points', {
        updates
      })
      return response.data
    } catch (error) {
      // Return mock success response
      return {
        successful_updates: updates.length,
        failed_updates: 0,
        errors: [],
        total_processed: updates.length,
      }
    }
  }

  static async exportBalancingReport(
    filters: BalancingFilters = {},
    format: 'excel' | 'csv' = 'excel'
  ): Promise<Blob> {
    try {
      const params: any = { format }
      if (filters.category_id) params.category_id = filters.category_id
      if (filters.brand_id) params.brand_id = filters.brand_id
      if (filters.warehouse_id) params.warehouse_id = filters.warehouse_id
      if (filters.stock_status && filters.stock_status !== 'all') params.stock_status = filters.stock_status

      const response = await apiClient.get('/admin/inventory/balancing/export', {
        params,
        responseType: 'blob'
      })
      return response.data
    } catch (error) {
      // Create mock CSV data
      const csvContent = `SKU,Product Name,Current Stock,Reorder Point,Suggested Order Quantity,Sales Velocity
PROD-001-RED-M,Premium T-Shirt,45,75,100,medium
PROD-002-BLUE-L,Cotton Jeans,15,60,80,medium
PROD-003-BLACK-S,Leather Jacket,0,20,30,slow`
      
      return new Blob([csvContent], { type: 'text/csv' })
    }
  }
}